import { supabase } from './supabase'
import { logError, trackEvent } from './monitoring'
import { DEBUG } from './config'
import { calculateProgress } from './queries'
import { calculateOnboardingProgress } from './onboardingProgress'

export interface DashboardKPIs {
  active: number
  completed: number
  complianceRate: number
  needsAttention: number
  pendingInvitations: number
}

export interface NextAction {
  id: string
  type: 'overdue_tasks' | 'pending_invites' | 'expiring_compliance'
  title: string
  count: number
  priority: 'high' | 'medium' | 'low'
  action: string
  dismissed?: boolean
}

export interface ComplianceOverview {
  compliant: number
  overdue: number
  expiring: number
  total: number
}

export interface ActivityItem {
  id: string
  type: 'employee_added' | 'task_completed' | 'invite_sent' | 'invite_accepted' | 'document_uploaded'
  title: string
  description: string
  timestamp: string
  employee_id?: string
  task_id?: string
  actor_name: string
  linkable: boolean
}

export interface UpcomingDeadline {
  id: string
  title: string
  employee_name: string
  employee_id: string
  due_date: string
  type: 'onboarding' | 'compliance'
  is_overdue: boolean
  priority: 'high' | 'medium' | 'low'
}

export interface HealthScore {
  score: number
  trend: 'up' | 'down' | 'stable'
  factors: {
    overdue_weight: number
    velocity: number
    upcoming_risk: number
  }
}

export interface TeamMember {
  id: string
  name: string
  email: string
  position: string
  department?: string
  onboarding_progress: number
  compliance_status: number
  onboarding_status: 'active' | 'completed'
  avatar_color: string
  created_at: string
  updated_at: string
}

export interface DashboardData {
  kpis: DashboardKPIs
  nextActions: NextAction[]
  compliance: ComplianceOverview
  recentActivity: ActivityItem[]
  upcomingDeadlines: UpcomingDeadline[]
  healthScores: Map<string, HealthScore>
  organizationHealth: HealthScore
  teamMembers: TeamMember[]
}

function debugLog(message: string, data?: any) {
  if (DEBUG) {
    console.debug(`[Dashboard] ${message}`, data)
  }
}

export async function fetchDashboardData(adminId: string): Promise<DashboardData> {
  debugLog('Fetching dashboard data', { adminId })
  
  try {
    // Batch all queries in parallel for performance
    const [
      employeesResult,
      tasksResult,
      invitationsResult,
      filesResult,
      teamMembersResult
    ] = await Promise.allSettled([
      // Employees with basic info
      supabase
        .from('employees')
        .select('id, name, email, position, department, start_date, status, onboarding_status, onboarding_completed_at, portal_access_status, created_at')
        .eq('admin_id', adminId)
        .eq('status', 'active'),
      
      // All tasks for analytics
      supabase
        .from('tasks')
        .select(`
          id, title, employee_id, status, due_date, completed_at, created_at, is_compliance, expires_at,
          completion_mode, review_status, action_type, owner_type,
          employees!inner(name, admin_id)
        `)
        .eq('employees.admin_id', adminId),
      
      // Pending invitations
      supabase
        .from('employee_invitations')
        .select('id, email, name, status, created_at, expires_at')
        .eq('admin_id', adminId)
        .eq('status', 'pending'),
      
      // Recent file uploads
      supabase
        .from('employee_files')
        .select(`
          id, file_name, uploaded_at, category,
          employees!inner(name, admin_id),
          uploader:uploaded_by(name)
        `)
        .eq('employees.admin_id', adminId)
        .order('uploaded_at', { ascending: false })
        .limit(10),
      
      // Team members with progress calculation
      supabase
        .from('employees')
        .select(`
          id, name, email, position, department, start_date,
          onboarding_status, created_at, updated_at
        `)
        .eq('admin_id', adminId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
    ])

    // Extract data with error handling
    const employees = employeesResult.status === 'fulfilled' && !employeesResult.value.error 
      ? employeesResult.value.data || [] 
      : []
    
    const tasks = tasksResult.status === 'fulfilled' && !tasksResult.value.error 
      ? tasksResult.value.data || [] 
      : []
    
    const invitations = invitationsResult.status === 'fulfilled' && !invitationsResult.value.error 
      ? invitationsResult.value.data || [] 
      : []
    
    const files = filesResult.status === 'fulfilled' && !filesResult.value.error 
      ? filesResult.value.data || [] 
      : []
    
    const teamMembersData = teamMembersResult.status === 'fulfilled' && !teamMembersResult.value.error 
      ? teamMembersResult.value.data || [] 
      : []

    // Calculate team member progress using consistent utility
    const teamMembers: TeamMember[] = teamMembersData.map((member) => {
      const memberTasks = tasks.filter(task => task.employee_id === member.id)
      const memberProgress = calculateOnboardingProgress(memberTasks)

      // Generate avatar color
      const colors = [
        'bg-[#1F2A44]', 'bg-green-500', 'bg-[#1F2A44]', 'bg-pink-500', 
        'bg-indigo-500', 'bg-yellow-500', 'bg-red-500', 'bg-teal-500'
      ]
      const avatarColor = colors[member.name.charCodeAt(0) % colors.length]

      return {
        id: member.id,
        name: member.name,
        email: member.email,
        position: member.position,
        department: member.department,
        onboarding_progress: memberProgress.onboarding.companyReady.percent,
        compliance_status: memberProgress.compliance.companyReady.percent,
        onboarding_status: member.onboarding_status,
        avatar_color: avatarColor,
        created_at: member.created_at,
        updated_at: member.updated_at
      }
    })


    // Calculate KPIs
    const activeEmployees = employees.filter(emp => emp.onboarding_status === 'active').length
    const completedEmployees = employees.filter(emp => emp.onboarding_status === 'completed').length
    const totalEmployees = employees.length
    
    const complianceTasks = tasks.filter(task => task.is_compliance)
    const complianceProgress = calculateOnboardingProgress(complianceTasks)
    const complianceRate = complianceProgress.compliance.companyReady.percent

    const overdueTasks = tasks.filter(task => 
      task.status !== 'completed' && 
      task.due_date && 
      new Date(task.due_date) < new Date()
    ).length

    const kpis: DashboardKPIs = {
      active: activeEmployees,
      completed: completedEmployees,
      complianceRate,
      needsAttention: overdueTasks,
      pendingInvitations: invitations.length
    }

    // Calculate next actions
    const nextActions: NextAction[] = []
    
    if (overdueTasks > 0) {
      nextActions.push({
        id: 'overdue-tasks',
        type: 'overdue_tasks',
        title: `${overdueTasks} overdue onboarding tasks`,
        count: overdueTasks,
        priority: 'high',
        action: 'Review overdue tasks',
        dismissed: false
      })
    }

    if (invitations.length > 0) {
      nextActions.push({
        id: 'pending-invites',
        type: 'pending_invites',
        title: `${invitations.length} invites not accepted`,
        count: invitations.length,
        priority: 'medium',
        action: 'Follow up on invitations',
        dismissed: false
      })
    }

    const expiringCompliance = complianceTasks.filter(task => 
      task.expires_at && 
      new Date(task.expires_at) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) &&
      new Date(task.expires_at) > new Date()
    ).length

    if (expiringCompliance > 0) {
      nextActions.push({
        id: 'expiring-compliance',
        type: 'expiring_compliance',
        title: `${expiringCompliance} compliance items expiring in 7 days`,
        count: expiringCompliance,
        priority: 'medium',
        action: 'Review expiring compliance',
        dismissed: false
      })
    }

    // Compliance overview
    const overdueCompliance = complianceTasks.filter(task => 
      task.status !== 'completed' && 
      task.due_date && 
      new Date(task.due_date) < new Date()
    ).length

    const compliance: ComplianceOverview = {
      compliant: completedCompliance,
      overdue: overdueCompliance,
      expiring: expiringCompliance,
      total: complianceTasks.length
    }

    // Recent activity
    const recentActivity: ActivityItem[] = []
    
    // Add employee additions
    employees.slice(0, 5).forEach(emp => {
      recentActivity.push({
        id: `emp-${emp.id}`,
        type: 'employee_added',
        title: `${emp.name} joined the team`,
        description: `Added as ${emp.position}`,
        timestamp: emp.created_at,
        employee_id: emp.id,
        actor_name: 'System',
        linkable: true
      })
    })

    // Add task completions
    tasks
      .filter(task => task.completed_at)
      .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())
      .slice(0, 5)
      .forEach(task => {
        recentActivity.push({
          id: `task-${task.id}`,
          type: 'task_completed',
          title: `${task.employees.name} completed "${task.title}"`,
          description: formatTimeAgo(task.completed_at!),
          timestamp: task.completed_at!,
          employee_id: task.employee_id,
          task_id: task.id,
          actor_name: task.employees.name,
          linkable: true
        })
      })

    // Add file uploads
    files.forEach(file => {
      recentActivity.push({
        id: `file-${file.id}`,
        type: 'document_uploaded',
        title: `${file.employees.name} uploaded ${file.file_name}`,
        description: `${file.category} document`,
        timestamp: file.uploaded_at,
        employee_id: file.employees.id,
        actor_name: file.uploader?.name || 'Unknown',
        linkable: true
      })
    })

    // Sort and limit activity
    recentActivity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    const limitedActivity = recentActivity.slice(0, 20)

    // Upcoming deadlines
    const today = new Date()
    const sevenDaysFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
    
    const upcomingDeadlines: UpcomingDeadline[] = tasks
      .filter(task => 
        task.status !== 'completed' && 
        task.due_date &&
        new Date(task.due_date) <= sevenDaysFromNow
      )
      .map(task => {
        const dueDate = new Date(task.due_date!)
        const isOverdue = dueDate < today
        const isDueToday = dueDate.toDateString() === today.toDateString()
        
        return {
          id: task.id,
          title: task.title,
          employee_name: task.employees.name,
          employee_id: task.employee_id,
          due_date: task.due_date!,
          type: task.is_compliance ? 'compliance' : 'onboarding',
          is_overdue: isOverdue,
          priority: isOverdue ? 'high' : isDueToday ? 'medium' : 'low'
        }
      })
      .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())

    // Calculate health scores
    const healthScores = new Map<string, HealthScore>()
    const organizationHealth = calculateOrganizationHealth(employees, tasks)

    employees.forEach(employee => {
      const empTasks = tasks.filter(task => task.employee_id === employee.id)
      const health = calculateEmployeeHealth(employee, empTasks)
      healthScores.set(employee.id, health)
    })

    debugLog('Dashboard data calculated', {
      employees: employees.length,
      tasks: tasks.length,
      kpis,
      nextActions: nextActions.length,
      activity: limitedActivity.length,
      deadlines: upcomingDeadlines.length
    })

    trackEvent({
      event: 'dashboard_loaded',
      properties: {
        employee_count: employees.length,
        task_count: tasks.length,
        completion_rate: complianceRate
      },
      userId: adminId
    })

    return {
      kpis,
      nextActions,
      compliance,
      recentActivity: limitedActivity,
      upcomingDeadlines,
      healthScores,
      organizationHealth,
      teamMembers
    }

  } catch (error) {
    debugLog('Error fetching dashboard data', error)
    logError(error as Error, { action: 'fetchDashboardData', adminId })
    throw new Error('Failed to load dashboard data')
  }
}

function calculateEmployeeHealth(employee: any, tasks: any[]): HealthScore {
  const overdueTasks = tasks.filter(task => 
    task.status !== 'completed' && 
    task.due_date && 
    new Date(task.due_date) < new Date()
  )
  
  const completedTasks = tasks.filter(task => task.status === 'completed')
  const totalTasks = tasks.length
  
  // Calculate factors
  const overdueWeight = totalTasks > 0 ? (overdueTasks.length / totalTasks) * 100 : 0
  const completionRate = totalTasks > 0 ? (completedTasks.length / totalTasks) * 100 : 100
  
  // Calculate velocity (tasks completed per week since start)
  const startDate = new Date(employee.start_date)
  const weeksActive = Math.max(1, Math.ceil((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)))
  const velocity = completedTasks.length / weeksActive

  // Calculate upcoming risk
  const upcomingTasks = tasks.filter(task => 
    task.status !== 'completed' && 
    task.due_date && 
    new Date(task.due_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  )
  const upcomingRisk = upcomingTasks.length > 3 ? 30 : upcomingTasks.length * 10

  // Calculate overall score (0-100)
  let score = 100
  score -= overdueWeight * 2 // Overdue tasks heavily penalize
  score -= Math.max(0, 100 - completionRate) // Incomplete tasks penalize
  score -= upcomingRisk // Upcoming deadlines create risk
  score += Math.min(20, velocity * 5) // Good velocity boosts score

  score = Math.max(0, Math.min(100, Math.round(score)))

  // Determine trend (simplified)
  const recentCompletions = completedTasks.filter(task => 
    task.completed_at && 
    new Date(task.completed_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  ).length

  const trend = recentCompletions > 1 ? 'up' : overdueTasks.length > 2 ? 'down' : 'stable'

  return {
    score,
    trend,
    factors: {
      overdue_weight: overdueWeight,
      velocity,
      upcoming_risk: upcomingRisk
    }
  }
}

function calculateOrganizationHealth(employees: any[], tasks: any[]): HealthScore {
  if (employees.length === 0) {
    return { score: 100, trend: 'stable', factors: { overdue_weight: 0, velocity: 0, upcoming_risk: 0 } }
  }

  const totalOverdue = tasks.filter(task => 
    task.status !== 'completed' && 
    task.due_date && 
    new Date(task.due_date) < new Date()
  ).length

  const totalCompleted = tasks.filter(task => task.status === 'completed').length
  const totalTasks = tasks.length

  const overdueWeight = totalTasks > 0 ? (totalOverdue / totalTasks) * 100 : 0
  const completionRate = totalTasks > 0 ? (totalCompleted / totalTasks) * 100 : 100

  // Organization velocity (avg tasks completed per employee per week)
  const avgVelocity = employees.reduce((sum, emp) => {
    const empTasks = tasks.filter(task => task.employee_id === emp.id && task.status === 'completed')
    const startDate = new Date(emp.start_date)
    const weeksActive = Math.max(1, Math.ceil((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)))
    return sum + (empTasks.length / weeksActive)
  }, 0) / employees.length

  let score = 100
  score -= overdueWeight * 1.5
  score -= Math.max(0, 100 - completionRate) * 0.8
  score += Math.min(15, avgVelocity * 3)

  score = Math.max(0, Math.min(100, Math.round(score)))

  return {
    score,
    trend: score > 80 ? 'up' : score < 60 ? 'down' : 'stable',
    factors: {
      overdue_weight: overdueWeight,
      velocity: avgVelocity,
      upcoming_risk: 0
    }
  }
}

function formatTimeAgo(timestamp: string): string {
  try {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return date.toLocaleDateString('en-GB')
  } catch {
    return '—'
  }
}

export interface GlobalSearchResult {
  id: string
  type: 'employee' | 'task' | 'template' | 'document'
  title: string
  subtitle: string
  url: string
  employee_id?: string
}

export async function globalSearch(query: string, adminId: string, limit = 5): Promise<GlobalSearchResult[]> {
  if (!query.trim()) return []
  
  debugLog('Global search', { query, limit })
  
  try {
    const searchTerm = `%${query.toLowerCase()}%`
    const results: GlobalSearchResult[] = []

    // Search employees
    const { data: employees } = await supabase
      .from('employees')
      .select('id, name, email, position')
      .eq('admin_id', adminId)
      .or(`name.ilike.${searchTerm},email.ilike.${searchTerm},position.ilike.${searchTerm}`)
      .limit(limit)

    employees?.forEach(emp => {
      results.push({
        id: emp.id,
        type: 'employee',
        title: emp.name,
        subtitle: `${emp.position} • ${emp.email}`,
        url: `/dashboard?employee=${emp.id}`,
        employee_id: emp.id
      })
    })

    // Search tasks
    const { data: tasks } = await supabase
      .from('tasks')
      .select(`
        id, title, description,
        employees!inner(id, name, admin_id)
      `)
      .eq('employees.admin_id', adminId)
      .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
      .limit(limit)

    tasks?.forEach(task => {
      results.push({
        id: task.id,
        type: 'task',
        title: task.title,
        subtitle: `Task for ${task.employees.name}`,
        url: `/dashboard?employee=${task.employees.id}&task=${task.id}`,
        employee_id: task.employees.id
      })
    })

    // Search templates
    const { data: templates } = await supabase
      .from('task_templates')
      .select('id, name, description')
      .eq('tenant_id', adminId)
      .or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`)
      .limit(limit)

    templates?.forEach(template => {
      results.push({
        id: template.id,
        type: 'template',
        title: template.name,
        subtitle: template.description || 'Onboarding template',
        url: `/dashboard?tab=templates&template=${template.id}`
      })
    })

    // Search documents
    const { data: documents } = await supabase
      .from('employee_files')
      .select(`
        id, file_name, category,
        employees!inner(id, name, admin_id)
      `)
      .eq('employees.admin_id', adminId)
      .or(`file_name.ilike.${searchTerm},category.ilike.${searchTerm}`)
      .limit(limit)

    documents?.forEach(doc => {
      results.push({
        id: doc.id,
        type: 'document',
        title: doc.file_name,
        subtitle: `${doc.category} • ${doc.employees.name}`,
        url: `/dashboard?employee=${doc.employees.id}&tab=documents`,
        employee_id: doc.employees.id
      })
    })

    // Sort by relevance (exact matches first, then partial)
    results.sort((a, b) => {
      const aExact = a.title.toLowerCase().includes(query.toLowerCase()) ? 1 : 0
      const bExact = b.title.toLowerCase().includes(query.toLowerCase()) ? 1 : 0
      return bExact - aExact
    })

    debugLog('Search results', { query, count: results.length })
    
    trackEvent({
      event: 'dashboard_search',
      properties: { query: query.substring(0, 50), result_count: results.length },
      userId: adminId
    })

    return results.slice(0, limit)
  } catch (error) {
    debugLog('Search error', error)
    logError(error as Error, { action: 'globalSearch', query, adminId })
    return []
  }
}